/**
 * Reusable Google sync control — a SMALL trigger button that opens a modal dialog with the
 * Connect / Sync / Re-auth / Disconnect controls. Embeddable from anywhere (Calendar, Contacts,
 * Tasks, Profile). Talks to System A (/api/google/*). Display logic is the pure, unit-tested
 * googleSyncView module.
 *
 * Props:
 *   services  {string[]}  optional whitelist e.g. ['calendar'] — default Calendar+Contacts+Tasks
 *   onSynced  {function}  called with the sync response after a successful "Sync now"
 *   label     {string|false}  trigger text (default 'Sync'); pass false for icon-only
 */
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../auth';
import { deriveGoogleView, visibleServices, syncSummary } from './googleSyncView';
import './GoogleSyncCard.css';

function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function GoogleSyncCard({ services, onSynced, label }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/google/status');
      setStatus(res.ok ? await res.json() : { connected: false, error: 'Unable to load Google status' });
    } catch (e) {
      setStatus({ connected: false, error: 'Unable to load Google status' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close on Escape while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const connect = () => { window.location.href = '/auth/google/sync'; };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google sync? Your Calendar, Contacts and Tasks stay, but go local-only.')) return;
    setMessage(null);
    try {
      const res = await authFetch('/api/google/disconnect', { method: 'POST' });
      if (!res.ok) throw new Error('Disconnect failed');
      setStatus({ connected: false });
      setMessage({ type: 'success', text: 'Google disconnected — now local-only.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Disconnect failed.' });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await authFetch('/api/google/sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Google sync failed');
      setMessage(syncSummary(data));
      await load();
      if (onSynced) onSynced(data);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Google sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const v = deriveGoogleView(status);
  const rows = visibleServices(services);
  const dotClass = v.needsAttention ? 'attention' : v.connected ? 'connected' : 'off';

  return (
    <>
      <button
        type="button"
        className={`gsync-trigger ${dotClass}`}
        onClick={() => setOpen(true)}
        title="Google sync"
        aria-haspopup="dialog"
        data-testid="gsync-trigger"
      >
        <GoogleG />
        {label !== false && <span className="gsync-trigger-label">{label || 'Sync'}</span>}
        <span className="gsync-trigger-dot" aria-hidden="true" />
      </button>

      {open && (
        <div className="gsync-overlay" onClick={() => setOpen(false)}>
          <div
            className="gsync-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Google sync"
            onClick={(e) => e.stopPropagation()}
            data-testid="google-sync-dialog"
          >
            <div className="gsync-dialog-head">
              <span className="gsync-dot" aria-hidden="true" />
              <strong>{v.title}</strong>
              <button type="button" className="gsync-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>

            <div className="gsync-detail">
              {v.connected
                ? <span>{v.email || 'Google account connected'}{v.lastSync ? ` · Last sync: ${new Date(v.lastSync).toLocaleString()}` : ''}</span>
                : <span>Connect Google to sync {rows.map((r) => r.label).join(', ')} — both ways.</span>}
            </div>

            <div className="gsync-services">
              {rows.map((r) => (
                <span key={r.key} className={`gsync-chip ${v.connected ? 'on' : 'off'}`}>
                  <span className="gsync-chip-icon" aria-hidden="true">{r.icon}</span>{r.label}
                </span>
              ))}
            </div>

            {v.lastError && <div className="gsync-message error">{v.lastError}</div>}
            {message && <div className={`gsync-message ${message.type}`}>{message.text}</div>}

            <div className="gsync-dialog-actions">
              {v.connected ? (
                <>
                  <button type="button" className="gsync-btn primary" onClick={syncNow} disabled={syncing} data-testid="gsync-sync">
                    {syncing ? 'Syncing…' : 'Sync now'}
                  </button>
                  <button type="button" className="gsync-btn" onClick={connect} data-testid="gsync-reauth">Re-auth</button>
                  <button type="button" className="gsync-btn danger" onClick={disconnect} data-testid="gsync-disconnect">Disconnect</button>
                </>
              ) : (
                <button type="button" className="gsync-btn primary" onClick={connect} data-testid="gsync-connect">Connect Google</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
